import React, { useMemo } from 'react';
import { Player } from '@/types/tournament';
import { useTournament } from '@/hooks/use-tournament';
import { SharedResultsEntry } from './shared/SharedResultsEntry';
import { TeamAmericanoAdapter } from '@/adapters/tournament-adapters';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Users, Trophy, History, ArrowLeft, RotateCcw, Flag } from 'lucide-react';
import { TeamAmericanoLeaderboard } from './TeamAmericanoLeaderboard';
import { RoundHistoryDialog } from './RoundHistoryDialog';
import { useLanguage } from '@/hooks/use-language';

interface TeamAmericanoIntegratedProps {
  participants: Player[];
}

export function TeamAmericanoIntegrated({ participants }: TeamAmericanoIntegratedProps) {
  const { language } = useLanguage();
  const {
    tournamentState,
    resetTournament,
    finishAmericanoRound,
    goToPreviousRound,
    finishTournament
  } = useTournament();

  const [showHistory, setShowHistory] = React.useState(false);
  const [showLeaderboard, setShowLeaderboard] = React.useState(false);

  const adapter = useMemo(
    () => new TeamAmericanoAdapter(tournamentState, finishAmericanoRound),
    [tournamentState, finishAmericanoRound]
  );

  const shouldShowLeaderboard = showLeaderboard || tournamentState.isFinished || !tournamentState.isRoundActive;

  // Calculate team leaderboard
  const teamLeaderboard = useMemo(() => {
    const teams = tournamentState.teamAmericanoFixedTeams || [];
    
    return teams.map((team, index) => {
      const player1 = tournamentState.americanoPlayers.find(p => p.id === team.player1.id);
      const player2 = tournamentState.americanoPlayers.find(p => p.id === team.player2.id);
      
      const totalPoints = (player1?.points || 0) + (player2?.points || 0);
      const gamesPlayed = Math.max(player1?.gamesPlayed || 0, player2?.gamesPlayed || 0);
      
      return {
        rank: index + 1,
        teamName: `Team ${index + 1}`,
        player1Name: team.player1.name,
        player2Name: team.player2.name,
        totalPoints,
        gamesPlayed
      };
    }).sort((a, b) => b.totalPoints - a.totalPoints)
      .map((team, index) => ({ ...team, rank: index + 1 }));
  }, [tournamentState.teamAmericanoFixedTeams, tournamentState.americanoPlayers]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
            <div className="flex items-center gap-2">
              <Trophy className="h-6 w-6 text-primary" />
              <div>
                <CardTitle>Team Americano Tournament</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Round {tournamentState.currentRound} of {tournamentState.totalRounds}
                </p>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              {tournamentState.currentRound > 1 && !tournamentState.isFinished && (
                <Button variant="outline" size="sm" onClick={goToPreviousRound}>
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  {language === 'no' ? 'Forrige runde' : 'Previous Round'}
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={() => setShowLeaderboard(!showLeaderboard)}>
                <Trophy className="h-4 w-4 mr-1" />
                {showLeaderboard ? (language === 'no' ? 'Skjul' : 'Hide') : (language === 'no' ? 'Vis' : 'Show')} 
                {' '}{language === 'no' ? 'Resultater' : 'Leaderboard'}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowHistory(true)}>
                <History className="h-4 w-4 mr-1" />
                {language === 'no' ? 'Historikk' : 'History'}
              </Button>
              {!tournamentState.isRoundActive && !tournamentState.isFinished && (
                <Button size="sm" onClick={finishTournament}>
                  <Flag className="h-4 w-4 mr-1" />
                  {language === 'no' ? 'Fullfør' : 'Finish'}
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={() => resetTournament()}>
                <RotateCcw className="h-4 w-4 mr-1" />
                {language === 'no' ? 'Nullstill' : 'Reset'}
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Display Fixed Teams */}
      {tournamentState.teamAmericanoFixedTeams && tournamentState.teamAmericanoFixedTeams.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-4">
              <Users className="h-5 w-5" />
              <h3 className="font-semibold">Fixed Teams</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {tournamentState.teamAmericanoFixedTeams.map((team, index) => (
                <div key={index} className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                  <Badge variant="outline">Team {index + 1}</Badge>
                  <span className="text-sm">
                    {team.player1.name} & {team.player2.name}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results Entry or Leaderboard */}
      {shouldShowLeaderboard ? (
        <TeamAmericanoLeaderboard teams={teamLeaderboard} />
      ) : (
        <SharedResultsEntry
          adapter={adapter}
          currentRound={tournamentState.currentRound}
          totalRounds={tournamentState.totalRounds}
        />
      )}

      {/* Round History Dialog */}
      <RoundHistoryDialog
        open={showHistory}
        onOpenChange={setShowHistory}
      />
    </div>
  );
}
